import Guid from "@/app/types/common/guid";
import { MenuItem } from "@/app/ui/navigation/types";
import type { Location } from "vue-router";
import type { Dictionary } from "vue-router/types/router";
import { EnumUserPermission } from "@/app/services/generated/enums/enumUserPermission.generated";
import { hasPermissionsUsingUserStore } from "@/components/pages/administration/user_roles/UserAccessHandler";
import { EnumNoun } from "@/app/types/common/enumNoun";

export type CreateProductType = "product" | "auto-assembled";

export const stockMenu = {
  root: "navigation_stock_group",
  materials: {
    list: "material_list",
    inStock: "materials_in_stock",
    create: "create_material",
    createLocation: (): Location => ({ name: stockMenu.materials.create }),
    duplicateMaterial: (materialId: Guid): Location => ({
      name: stockMenu.materials.create,
      query: {
        duplicate_from_material_id: materialId.toString(),
      },
    }),
  },
  products: {
    list: "product_list",
    inStock: "products_in_stock",
    create: "create_product",
    createLocation: (
      stockItemType: CreateProductType = "product"
    ): Location => ({
      name: stockMenu.products.create,
      params: { stockItemType },
    }),
    duplicateProduct: (productId: Guid): Location => ({
      name: stockMenu.products.create,
      query: {
        duplicate_from_product_id: productId.toString(),
      },
    }),
  },
  autoAssembled: {
    stock: "auto_assembled_in_stock",
  },
  productBrands: {
    list: "product_brands",
    listLocation: (): Location => ({
      name: stockMenu.productBrands.list,
    }),
    edit: "stock_product_brands_edit",
    editLocation: (id: Guid, isEditing: boolean): Location => ({
      name: stockMenu.productBrands.edit,
      params: {
        id: id.toString(),
        mode: isEditing ? "edit" : "view",
      },
    }),
    createLocation: (): Location => ({
      name: stockMenu.productBrands.edit,
    }),
  },
  purchaseOrdering: {
    list: "purchase_order_list",
    listPurchaseOrders: (): Location => ({
      name: stockMenu.purchaseOrdering.list,
    }),
    create: "purchase_order_create",
    edit: "purchase_order_edit",
    viewPurchaseOrder: (id: Guid): Location => ({
      name: stockMenu.purchaseOrdering.edit,
      params: { id: id.toString() },
    }),
  },

  stockMovements: {
    root: "navigation_stock_movements_group",
    stockTransfer: {
      list: "stock_transfer_list",
      create: "stock_transfer_create",
      edit: "stock_transfer_edit",
      listTransfers: (): Location => ({
        name: stockMenu.stockMovements.stockTransfer.list,
      }),
      createStockTransfer: (
        fromStockLocationId?: Guid,
        stockItemId?: Guid,
        batch?: string
      ): Location => {
        let params: Dictionary<string> = {};

        if (fromStockLocationId) {
          params.fromStockLocationId = fromStockLocationId.toString();
        }

        if (stockItemId) {
          params.stockItemId = stockItemId.toString();
        }

        if (batch) {
          params.batch = batch;
        }

        return {
          name: stockMenu.stockMovements.stockTransfer.edit,
          params,
        };
      },
      editStockTransfer: (id: Guid, isEditing: boolean): Location => ({
        name: stockMenu.stockMovements.stockTransfer.edit,
        params: { id: id.toString(), mode: isEditing ? "edit" : "view" },
      }),
    },
    stockAdjustment: {
      list: "stock_adjustment_list",
      listLocation: (): Location => ({
        name: stockMenu.stockMovements.stockAdjustment.list,
      }),
      view: "stock_adjustment_view",
      create: "stock_adjustment_create",
      createLocation: (
        stockLocationId?: Guid,
        stockItemId?: Guid
      ): Location => {
        let params: Dictionary<string> = {};
        if (stockLocationId) {
          params.location = stockLocationId?.toString();
        }
        if (stockItemId) {
          params.stockItemId = stockItemId?.toString();
        }
        return {
          name: stockMenu.stockMovements.stockAdjustment.create,
          params,
        };
      },
      viewStockAdjustment: (id: Guid): Location => ({
        name: stockMenu.stockMovements.stockAdjustment.view,
        params: { id: id.toString() },
      }),
    },
  },
  stock: {
    view: "stock_details",
    viewLocation: (stockItemId: Guid): Location => ({
      name: "stock_details",
      params: {
        id: stockItemId.toString(),
      },
    }),
    pricing: "stock_pricing",
    labelling: "stock_labelling",
    documents: "stcok_item_documents",
    openOrders: "stock_open_orders",
    tradePortal: "stock_trade_portal",
  },
  stockTake: {
    list: "stock_takes",
    create: "stock_take_create",
    view: "stock_take_view",
    viewLocation: (stockItemId: Guid): Location => ({
      name: "stock_take_view",
      params: {
        id: stockItemId.toString(),
      },
    }),
  },
  stockReplenishment: "stock_replenishment",
  viewStockReplenishment: (): Location => ({
    name: stockMenu.stockReplenishment,
  }),
  stockAudit: "stock_audit",
  viewStockAudit: (opts?: {
    stockItemId?: Guid;
    stockLocationId?: Guid;
  }): Location => ({
    name: stockMenu.stockAudit,
    query: {
      stockItemId: opts?.stockItemId?.toString(),
      stockLocationId: opts?.stockLocationId?.toString(),
    },
  }),
  stockRequirement: "stock_requirement",
  stockFutureTransactions: "stock_future_transactions",
  viewFutureStockTransaction: (
    stockItemId: Guid,
    stockLocationId: Guid
  ): Location => ({
    name: stockMenu.stockFutureTransactions,
    query: {
      item: stockItemId.toString(),
      location: stockLocationId.toString(),
    },
  }),
  historicStock: "historic_stock",
  stockBatches: "stock_batches",
  stockBatchesList: (): Location => ({ name: stockMenu.stockBatches }),
};

export const buildStockMenuItems: () => MenuItem[] = () => [
  MenuItem.forBranch({
    name: stockMenu.root,
    icon: "local_bar",
    visible: () =>
      hasPermissionsUsingUserStore([EnumUserPermission.menuStockView]),
    subMenus: [
      // Materials
      MenuItem.fromOpts({
        name: stockMenu.materials.list,
        url: "/stock/materials/list",
        view: () =>
          import(
            /* webpackChunkName: "stock" */ "@/components/pages/stock/items/ListMaterials"
          ),
      }),
      MenuItem.fromOpts({
        name: stockMenu.materials.inStock,
        icon: "create",
        url: "/stock/materials_in_stock",
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "stock" */ "@/components/pages/stock/stockenquiry/MaterialsInStock.vue"
          ),
      }),
      MenuItem.fromOpts({
        name: stockMenu.materials.create,
        url: "/stock/materials/create",
        breadcrumbParentName: stockMenu.materials.list,
        view: () =>
          import(
            /* webpackChunkName: "stock" */ "@/components/pages/stock/create/CreateMaterialWizard.vue"
          ),
        visible: false,
      }),
      //   Products
      MenuItem.fromOpts({
        name: stockMenu.products.list,
        url: "/stock/products/list",
        view: () =>
          import(
            /* webpackChunkName: "stock" */ "@/components/pages/stock/items/ListProducts"
          ),
      }),
      MenuItem.fromOpts({
        name: stockMenu.products.inStock,
        icon: "create",
        url: "/stock/products_in_stock",
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "stock" */ "@/components/pages/stock/stockenquiry/ProductsInStock.vue"
          ),
      }),
      MenuItem.fromOpts({
        name: stockMenu.autoAssembled.stock,
        icon: "create",
        url: "/stock/auto_assembled_stock",
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "stock" */ "@/components/pages/stock/stockenquiry/AutoAssembledStock.vue"
          ),
      }),

      MenuItem.fromOpts({
        name: stockMenu.productBrands.list,
        url: "/stock/productbrands",
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "stock" */ "@/components/pages/stock/brands/ListProductBrands"
          ),
      }),
      MenuItem.fromOpts({
        name: stockMenu.productBrands.edit,
        url: "/stock/productbrands/:id?/:mode?",
        breadcrumbParentName: stockMenu.productBrands.list,
        view: () =>
          import(
            /* webpackChunkName: "stock" */ "@/components/pages/stock/brands/EditProductBrands.vue"
          ),
        visible: false,
      }),
      MenuItem.fromOpts({
        name: stockMenu.purchaseOrdering.list,
        url: "/purchaseorders",
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "sales" */ "@/components/pages/purchasing/ListPurchaseOrders.vue"
          ),
      }),
      MenuItem.fromOpts({
        name: stockMenu.purchaseOrdering.create,
        url: "/purchaseorders/create",
        breadcrumbParentName: stockMenu.purchaseOrdering.list,
        view: () =>
          import(
            /* webpackChunkName: "sales" */ "@/components/pages/purchasing/EditPurchaseOrder.vue"
          ),
        visible: false,
      }),
      MenuItem.fromOpts({
        name: stockMenu.purchaseOrdering.edit,
        url: "/purchaseorders/:id",
        breadcrumbParentName: stockMenu.purchaseOrdering.list,
        view: () =>
          import(
            /* webpackChunkName: "sales" */ "@/components/pages/purchasing/EditPurchaseOrder.vue"
          ),
        visible: false,
        componentProps: (route) => ({
          purchaseOrderIdString: route.params.id,
        }),
      }),
      MenuItem.fromOpts({
        name: stockMenu.stockReplenishment,
        url: "/stockreplenishment",
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "stock" */ "@/components/pages/stock/stockreplenishment/StockReplenishmentPage.vue"
          ),
      }),
      MenuItem.fromOpts({
        name: stockMenu.stockRequirement,
        url: "/stockrequirement",
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "reporting" */ "@/components/pages/stock/stockrequirement/StockRequirement.vue"
          ),
      }),
      MenuItem.forBranch({
        name: stockMenu.stockMovements.root,
        subMenus: [
          MenuItem.fromOpts({
            name: stockMenu.stockMovements.stockTransfer.list,
            url: "/stock/stocktransfers/list",
            breadcrumbParentName: stockMenu.stockMovements.root,
            view: (): Promise<any> =>
              import(
                /* webpackChunkName: "stock" */ "@/pages/stock/stocktransfers/StockTransfersListPage.vue"
              ),
          }),
          MenuItem.fromOpts({
            name: stockMenu.products.create,
            url: "/stock/products/create",
            breadcrumbParentName: stockMenu.products.list,
            view: () =>
              import(
                /* webpackChunkName: "stock" */ "@/components/pages/stock/create/CreateProductWizard.vue"
              ),
            visible: false,
            componentProps: (route) => ({
              stockItemType: route.params.stockItemType,
            }),
          }),
          MenuItem.fromOpts({
            name: stockMenu.stockMovements.stockTransfer.edit,
            url: "/stock/stocktransfer/:id?/:mode?",
            breadcrumbParentName: stockMenu.stockMovements.stockTransfer.list,
            view: (): Promise<any> =>
              import(
                /* webpackChunkName: "stock" */ "@/components/pages/stock/transfer/EditTransfer.vue"
              ),
            visible: false,
            componentProps: (route) => {
              return {
                fromStockLocationId: route.params.fromStockLocationId,
                stockItemId: route.params.stockItemId,
                batch: route.params.batch,
              };
            },
          }),
          MenuItem.fromOpts({
            name: stockMenu.stockMovements.stockAdjustment.list,
            url: "/stock/adjustments/list",
            breadcrumbParentName: stockMenu.stockMovements.root,
            view: (): Promise<any> =>
              import(
                /* webpackChunkName: "stock" */ "@/components/pages/stock/stockadjustment/ListStockAdjustments.vue"
              ),
          }),
          MenuItem.fromOpts({
            name: stockMenu.stockMovements.stockAdjustment.create,
            icon: "",
            url: "/stock/adjustments/create",
            breadcrumbParentName: stockMenu.stockMovements.stockAdjustment.list,
            view: (): Promise<any> =>
              import(
                /* webpackChunkName: "stock" */ "@/components/pages/stock/stockadjustment/EditStockAdjustment.vue"
              ),
            visible: false,
          }),
          MenuItem.fromOpts({
            name: stockMenu.stockMovements.stockAdjustment.view,
            icon: "",
            url: "/stock/adjustments/:id",
            breadcrumbParentName: stockMenu.stockMovements.stockAdjustment.list,
            view: (): Promise<any> =>
              import(
                /* webpackChunkName: "stock" */ "@/components/pages/stock/stockadjustment/EditStockAdjustment.vue"
              ),
            visible: false,
          }),
        ],
      }),

      MenuItem.fromOpts({
        name: "",
        url: "/stock/:id",
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "stock" */ "@/components/pages/stock/items/ViewStock.vue"
          ),
        visible: false,
        routeMetadata: {
          routeKey: (route) => "/stock/" + route.params.id,
        },
        childRoutes: [
          {
            name: stockMenu.stock.view,
            path: "",
            component: () =>
              import(
                /* webpackChunkName: "stock" */ "@/components/pages/stock/viewstock/StockDetailsTab.vue"
              ),
          },
          {
            name: stockMenu.stock.pricing,
            path: "pricing",
            component: () =>
              import(
                /* webpackChunkName: "stock" */ "@/components/pages/stock/viewstock/StockPricingTab.vue"
              ),
          },
          {
            name: stockMenu.stock.labelling,
            path: "labelling",
            component: () =>
              import(
                /* webpackChunkName: "stock" */ "@/components/pages/stock/viewstock/StockLabellingTab.vue"
              ),
          },
          {
            name: stockMenu.stock.documents,
            path: "documents",
            component: () =>
              import(
                /* webpackChunkName: "stock" */ "@/components/pages/stock/viewstock/StockDocumentStoreTab.vue"
              ),
          },
          {
            name: stockMenu.stock.openOrders,
            path: "openorders",
            component: () =>
              import(
                /* webpackChunkName: "stock" */ "@/components/pages/stock/viewstock/StockOpenOrdersTab.vue"
              ),
          },
          {
            name: stockMenu.stock.tradePortal,
            path: "trade-portal",
            component: () =>
              import(
                /* webpackChunkName: "stock" */ "@/pages/trade_portal/item_settings/TradePortalItemTab.vue"
              ),
          },
        ],
      }),

      MenuItem.fromOpts({
        name: stockMenu.stockTake.list,
        url: "/stocktake",
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "stock" */ "@/components/pages/stock/stocktake/ListStockTake.vue"
          ),
      }),
      MenuItem.fromOpts({
        name: stockMenu.stockTake.create,
        url: "/stocktake/create/:id?",
        breadcrumbParentName: stockMenu.stockTake.list,
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "stock" */ "@/components/pages/stock/stocktake/CreateStockTake.vue"
          ),
        visible: false,
        componentProps: (route) => ({
          stockTakeId: Guid.tryParse(route.params.id) ?? undefined,
        }),
      }),
      MenuItem.fromOpts({
        name: stockMenu.stockTake.view,
        url: "/stocktake/:id",
        breadcrumbParentName: stockMenu.stockTake.list,
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "stock" */ "@/components/pages/stock/stocktake/ViewStockTake.vue"
          ),
        visible: false,
        componentProps: (route) => ({
          stockTakeId: Guid.tryParse(route.params.id) ?? undefined,
        }),
      }),
      MenuItem.fromOpts({
        name: stockMenu.stockAudit,
        url: "/reporting/stockaudit",
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "reporting" */ "@/components/pages/reporting/StockAuditReport.vue"
          ),
      }),
      MenuItem.fromOpts({
        name: stockMenu.stockFutureTransactions,
        url: "/futuretransactions",
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "stock" */ "@/components/pages/stock/items/ListFutureTransactions"
          ),
      }),
      MenuItem.fromOpts({
        name: stockMenu.historicStock,
        url: "/historic-stock",
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "stock" */ "@/components/pages/stock/historicstock/HistoricStockLevelPage.vue"
          ),
      }),
      MenuItem.fromOpts({
        name: stockMenu.stockBatches,
        label: EnumNoun.stockBatch.messages.Plural(),
        url: "/stockbatches/stockbatchlist",
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "stock" */ "@/pages/stock/stockbatches/StockBatchListPage.vue"
          ),
      }),
    ],
  }),
];
